import { _getLogger } from '@doxyme/analytics';
import ExtensionCapability from '../../capability';
import { type Message, Specs, attachHandler } from '@doxyme/extensions-common';
import type ExtensionInstance from '../../instance';

const logger = _getLogger('navigationInterception', {
  component: 'extensions',
  lib: 'bridge',
});

const { NavigationInterception } = Specs;
type Constraints = Specs.NavigationInterception.Constraints;
type NavigationAddPayload = Specs.NavigationInterception.NavigationAddPayload;
type NavigationRemovePayload =
  Specs.NavigationInterception.NavigationRemovePayload;
type NavigationManualTriggerPayload =
  Specs.NavigationInterception.NavigationManualTriggerPayload;

export type OnNavigationInterceptionAddHandler = (
  payload: NavigationAddPayload,
  sendMessage: (message: Message) => void,
) => void;

type OnNavigationInterceptionRemoveHandler = (
  payload: NavigationRemovePayload,
  sendMessage: (message: Message) => void,
) => void;

export type OnNavigationInterceptionManualTriggerHandler = (
  payload: NavigationManualTriggerPayload,
) => void;

export default class NavigationInterceptionCapability extends ExtensionCapability<Constraints> {
  static readonly id = NavigationInterception.id;

  protected readonly blockHandlers: OnNavigationInterceptionAddHandler[] = [];
  protected readonly allowHandlers: OnNavigationInterceptionRemoveHandler[] =
    [];
  protected readonly manualTriggerHandlers: OnNavigationInterceptionManualTriggerHandler[] =
    [];

  private readonly pendingManualTriggers = new Map<string, ExtensionInstance>();

  constructor() {
    super(NavigationInterception);

    this.handlers.push(
      [
        NavigationInterception.Actions.Add,
        this.addNavigationInterception.bind(this),
      ],
      [
        NavigationInterception.Actions.Remove,
        this.removeNavigationInterception.bind(this),
      ],
      [
        NavigationInterception.Actions.ManualTrigger,
        this.manualTriggerNavigationInterception.bind(this),
      ],
    );
  }

  // Method to block navigation
  private addNavigationInterception(
    payload: NavigationAddPayload,
    instance: ExtensionInstance,
  ) {
    for (const handler of this.blockHandlers) {
      handler(payload, (message: Message) =>
        this.handleMessages(instance, [message]),
      );
    }
  }

  private removeNavigationInterception(
    payload: NavigationRemovePayload,
    instance: ExtensionInstance,
  ) {
    for (const handler of this.allowHandlers) {
      handler(payload, (message: Message) =>
        this.handleMessages(instance, [message]),
      );
    }
  }

  private manualTriggerNavigationInterception(
    payload: NavigationManualTriggerPayload,
    instance: ExtensionInstance,
  ) {
    this.pendingManualTriggers.set(payload.id, instance);
    for (const handler of this.manualTriggerHandlers) {
      handler(payload);
    }
  }

  /**
   * Resolves a pending trigger by sending the decision back to the extension.
   */
  resolveManualTrigger(
    id: string,
    decision: Specs.NavigationInterception.NavigationDecision,
  ): void {
    const instance = this.pendingManualTriggers.get(id);
    if (!instance) {
      logger.warn(
        '[resolveManualTrigger] No pending trigger found, it may have already been resolved',
        { id, decision },
      );
      return;
    }

    instance.notify([
      NavigationInterception.Events.ManualTriggerResolved,
      { id, decision },
    ]);
    this.pendingManualTriggers.delete(id);
  }

  onAdd(handler: OnNavigationInterceptionAddHandler): () => void {
    return attachHandler<OnNavigationInterceptionAddHandler>(
      this.blockHandlers,
      handler,
    );
  }

  onRemove(handler: OnNavigationInterceptionRemoveHandler): () => void {
    return attachHandler<OnNavigationInterceptionRemoveHandler>(
      this.allowHandlers,
      handler,
    );
  }

  onManualTrigger(
    handler: OnNavigationInterceptionManualTriggerHandler,
  ): () => void {
    return attachHandler<OnNavigationInterceptionManualTriggerHandler>(
      this.manualTriggerHandlers,
      handler,
    );
  }
}
